import { useMutation } from '@tanstack/react-query';
import { handleApiResponse } from '@/functions';
import { useInvalidateQueries } from '@/hooks';
import { apiClient } from '@/components/api';
import type { IPromoCodeOffer, IPromoCodePayload } from '@/interfaces';
import routes from '@/lib/routes';

/* Create, edit, duplicate, stop or start again, and announce an offer. Each refreshes the list. */
export const useRequestPromoCodeActions = () => {
	const { invalidate } = useInvalidateQueries();
	const refresh = () => invalidate(routes.api.promoCodes.base);

	const create = useMutation({
		mutationFn: (data: IPromoCodePayload) =>
			apiClient.post<IPromoCodeOffer>(routes.api.promoCodes.base, data).then(handleApiResponse),
		onSuccess: refresh,
	});

	const update = useMutation({
		mutationFn: ({ id, data }: { id: string; data: Partial<IPromoCodePayload> }) =>
			apiClient.put(routes.api.promoCodes.byId(id), data).then(handleApiResponse),
		onSuccess: refresh,
	});

	const duplicate = useMutation({
		mutationFn: ({ id, data }: { id: string; data: Partial<IPromoCodePayload> & { code: string } }) =>
			apiClient.post<IPromoCodeOffer>(routes.api.promoCodes.duplicate(id), data).then(handleApiResponse),
		onSuccess: refresh,
	});

	const setDisabled = useMutation({
		mutationFn: ({ id, disabled }: { id: string; disabled: boolean }) =>
			apiClient.put(routes.api.promoCodes.disabled(id), { disabled }).then(handleApiResponse),
		onSuccess: refresh,
	});

	const announce = useMutation({
		mutationFn: (id: string) => apiClient.post(routes.api.promoCodes.announce(id)).then(handleApiResponse),
		onSuccess: refresh,
	});

	return {
		isLoading: create.isPending || update.isPending || duplicate.isPending || setDisabled.isPending || announce.isPending,
		triggerCreate: create.mutateAsync,
		triggerUpdate: update.mutateAsync,
		triggerDuplicate: duplicate.mutateAsync,
		triggerSetDisabled: setDisabled.mutateAsync,
		triggerAnnounce: announce.mutateAsync,
	};
};
